import { p as proxyCustomElement, H, c as createEvent, h } from './index.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, d as watchError, e as watchWarning, w as watchValue, f as renderFormField } from './p-Cg8AdZPK.js';
import { d as defineCustomElement$o } from './p-C_esi8gV.js';
import { d as defineCustomElement$n } from './p-Cd2X-6KG.js';
import { d as defineCustomElement$m } from './p-BedvZv52.js';
import { d as defineCustomElement$l } from './p-BN9xIVU9.js';
import { d as defineCustomElement$k } from './p-CkgLRZPl.js';
import { d as defineCustomElement$j } from './p-D-k_i6-R.js';
import { d as defineCustomElement$i } from './p-DZKBv4tL.js';
import { d as defineCustomElement$h } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$g } from './p-kuoYNbOB.js';
import { d as defineCustomElement$f } from './p-C3Ej010A.js';
import { d as defineCustomElement$e } from './p-ptCmZg09.js';
import { d as defineCustomElement$d } from './p-BVCTlSQE.js';
import { d as defineCustomElement$c } from './p-D4xpFZtu.js';
import { d as defineCustomElement$b } from './p-BkDQJ61k.js';
import { d as defineCustomElement$a } from './p-Cy8qWWQl.js';
import { d as defineCustomElement$9 } from './p-DLWyhg_S.js';
import { d as defineCustomElement$8 } from './p-B6RR6Z8w.js';
import { d as defineCustomElement$7 } from './p-DaiIyqo3.js';
import { d as defineCustomElement$6 } from './p-DE-WBrSS.js';
import { d as defineCustomElement$5 } from './p-m66-Ap9Q.js';
import { d as defineCustomElement$4 } from './p-aqq22Qbe.js';
import { d as defineCustomElement$3 } from './p-Bjt5CD4D.js';
import { d as defineCustomElement$2 } from './p-QtR5VUMp.js';

const keyValueInputCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;flex:1;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.key-value-input-container{container-type:inline-size;display:flex;flex-direction:column;gap:.5rem;max-width:var(--jw-container-md)}.key-value-inputs-wrapper{align-items:flex-start;align-self:stretch;display:flex;flex-direction:column;gap:.75rem}.key-value-inputs-wrapper wui-layout{flex:1}.key-value-input-item-row{align-items:center;align-self:stretch;display:flex;flex-direction:row;gap:.5rem}.close-button-container{min-width:1.5rem}";

const WuiKeyValueInput$1 = /*@__PURE__*/ proxyCustomElement(class WuiKeyValueInput extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.value = [{ key: '', value: '' }];
        this.required = false;
        this.validateInternal = (value) => {
            // External validation function runs first
            const externalValidationResult = this.validate?.(value);
            if (externalValidationResult) {
                return externalValidationResult;
            }
            // Run validation on all inputs
            const inputElms = Array.from(this.elm.shadowRoot?.querySelectorAll('wui-input') ?? []);
            if (!inputElms.length) {
                // Nothing to validate
                return;
            }
            // Let inner input handle validation
            for (const inputElm of inputElms) {
                inputElm.checkValidity();
            }
            const isInvalid = inputElms.some((inputElm) => inputElm.validity.valid);
            if (isInvalid) {
                return {
                    validationMessage: '', // Keep empty as the inner inputs will report their validity more specifically
                    validity: {
                        valid: false,
                    },
                };
            }
        };
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this, () => {
            if (!this.elm.shadowRoot) {
                return;
            }
            return this.elm.shadowRoot.querySelectorAll('wui-input');
        });
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        /**
         * If the input is disabled
         */
        this.disabled = false;
        /**
         * The field name for the key input
         */
        this.keyFieldName = 'Key';
        /**
         * The field name for the value input
         */
        this.valueFieldName = 'Value';
        this.setValue = (value) => {
            this.dirty = true;
            this.value = value;
            this.checkValidity();
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.handleAddItem = () => {
            this.setValue([...(this.value || []), { key: '', value: '' }]);
        };
        this.handleRemoveItem = (index) => {
            this.setValue(this.value?.filter((_, i) => i !== index));
        };
        this.handleChangeItem = (index, item, key) => {
            const emptyItem = { key: '', value: '' };
            if (!this.value) {
                this.setValue([{ ...emptyItem, ...item }]);
            }
            if (!this.value?.[index]) {
                this.setValue(this.value?.map((elm, i) => (i === index ? { ...emptyItem, ...item } : elm)));
            }
            this.setValue(this.value?.map((elm, i) => (i === index ? { ...elm, [key]: item[key] } : elm)));
        };
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    renderInner() {
        const keyAddon = this.keyInputs?.addon ?? 'Key';
        const valueAddon = this.valueInputs?.addon ?? 'Value';
        return (h("section", { class: "key-value-input-container" }, h("div", { class: "key-value-inputs-wrapper" }, this.value?.map((item, index) => (h("div", { class: "key-value-input-item-row" }, h("wui-layout", { type: "field-field-group", orientation: "horizontal", container: "lg" }, h("wui-input", { value: item.key, onInput: (e) => this.handleChangeItem(index, { key: e.target.value }, 'key'), canClear: false, disabled: this.disabled, ...this.keyInputs, addon: keyAddon, placeholder: this.keyInputs?.placeholder ?? `Enter ${keyAddon.toLowerCase()}`, validate: (value) => this.keyInputs?.validate(value, this.value) }), this.valueInputs?.variant === 'select' ? (h("wui-select-input", { value: item.value, onInput: (e) => this.handleChangeItem(index, { value: e.target.value }, 'value'), disabled: this.disabled, ...this.valueInputs, addon: valueAddon, placeholder: this.valueInputs?.placeholder ?? `Enter ${valueAddon.toLowerCase()}`, validate: (value) => this.valueInputs?.validate(value, this.value) })) : (h("wui-input", { value: item.value, onInput: (e) => this.handleChangeItem(index, { value: e.target.value }, 'value'), canClear: false, disabled: this.disabled, ...this.valueInputs, addon: valueAddon, placeholder: this.valueInputs?.placeholder ?? `Enter ${valueAddon.toLowerCase()}`, validate: (value) => this.valueInputs?.validate(value, this.value) }))), h("div", { class: "close-button-container" }, (this.value?.length ?? 0) > 1 && (h("wui-close-button", { onClick: () => this.handleRemoveItem(index), disabled: this.disabled }))))))), h("wui-button", { type: "tertiary", icon: "plus", label: "Add", onClick: this.handleAddItem, disabled: this.disabled })));
    }
    render() {
        return renderFormField(this, this.renderInner());
    }
    get elm() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"],
        "value": ["_handleValueChange"]
    }; }
    static get style() { return keyValueInputCss; }
}, [257, "wui-key-value-input", {
        "value": [1040],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "keyInputs": [16, "key-inputs"],
        "valueInputs": [16, "value-inputs"],
        "disabled": [4],
        "keyFieldName": [1, "key-field-name"],
        "valueFieldName": [1, "value-field-name"],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"],
        "value": ["_handleValueChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-key-value-input", "wui-badge", "wui-box", "wui-button", "wui-check-input", "wui-close-button", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-input-button", "wui-input-menu", "wui-layout", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-select-input", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-key-value-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiKeyValueInput$1);
            }
            break;
        case "wui-badge":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-input-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-input-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-select-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiKeyValueInput = WuiKeyValueInput$1;
const defineCustomElement = defineCustomElement$1;

export { WuiKeyValueInput, defineCustomElement };
